import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ServiceType } from '@common//enums/serviceType';
import { ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { getServiceActivities } from '@frontend/services/api/servicesApiService';
import { fetchJson } from '@frontend/store/fetch';
import { hasOneAuthorization } from '@frontend/utils/hasAuthorization';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IServiceGetOne } from '../../interfaces/serviceGetOne';
import { Loading } from '../../elements/common';
import { SideNavigationTemplate } from '../../templates/SideNavigationTemplate';
import { NzaServiceDeclarationRuleAddEdit } from '../nzaServiceDeclarationRules/NzaServiceDeclarationRuleAddEdit';
import { PatientInvoiceServicePrices } from '../patientInvoiceServicePrices/PatientInvoiceServicePrices';
import { ServiceGraderPool } from '../serviceGraderPools/ServiceGraderPool';
import { ServiceGroupSettingsList } from '../serviceGroupSettings/ServiceGroupSettingsList';
import { ServiceOrganizationSettingsList } from '../serviceOrganizationSettings/ServiceOrganizationSettingsList';
import { FundingServiceParts } from './fundingServiceParts/FundingServiceParts';
import { InsurerServiceDeclarationRulesList } from './insurerServiceDeclarationRulesList/InsurerServiceDeclarationRulesList';
import { PayableServiceActivitiesList } from './payableServiceActivities/PayableServiceActivitiesList';
import { ServiceBlock } from './ServiceBlock';
import { ServiceConsumerList } from './ServiceConsumerList';
import { ServiceDeclarationActivityList } from './serviceDeclarationActivityList/ServiceDeclarationActivityList';
import { ServiceProviderList } from './ServiceProviderList';
import { ServiceSupervisorList } from './ServiceSupervisorList';
import { ServiceWorkflow } from './ServiceWorkflow';
import SubsequentServiceList from './SubsequentServiceList';

interface ITab {
    content: JSX.Element;
    name: string;
    title: string;
}

export function ServiceDetails() {
    const translate = getLanguage();
    const { id } = useParams() as { id: string };
    const [service, setService] = useState<IServiceGetOne>();
    const [hasServiceActivities, setHasServiceActivities] = useState<boolean>(false);
    const abortSignal = useAbortSignal();

    useEffect(() => {
        loadServiceDetails(id);
    }, [id]);

    const tabs: ITab[] = [];
    if (service) {
        if (hasOneAuthorization(ManagerAuthorization.serviceRightsCareproviders)) {
            tabs.push(
                {
                    content: (
                        <ServiceProviderList
                            serviceCode={service.code}
                            serviceId={service.id}
                            serviceType={service.serviceType}
                        />
                    ),
                    name: 'providers',
                    title: translate.serviceProvider.providers,
                },
                {
                    content: <ServiceConsumerList serviceId={service.id} />,
                    name: 'consumers',
                    title: translate.serviceConsumer.consumers,
                },
                {
                    content: <ServiceSupervisorList serviceId={service.id} />,
                    name: 'supervisors',
                    title: translate.supervisors.supervisors,
                }
            );
        }

        if (hasOneAuthorization(ManagerAuthorization.serviceRightsOrganizations)) {
            tabs.push({
                content: (
                    <ServiceOrganizationSettingsList
                        filterId={id}
                        filterType="service"
                    />
                ),
                name: 'providerOrganizationSettings',
                title: translate.serviceProvider.serviceOrganizationSettings,
            });
        }
        if (hasOneAuthorization(ManagerAuthorization.serviceRightsGroups)) {
            tabs.push({
                content: <ServiceGroupSettingsList serviceId={service.id} />,
                name: 'groupSettings',
                title: translate.groupSettings.groupSettings,
            });
        }

        if (hasOneAuthorization(ManagerAuthorization.servicesRead)) {
            tabs.push(
                {
                    content: (
                        <SubsequentServiceList
                            serviceEpisodeCategoryName={
                                service.episodeCategory ? service.episodeCategory.name : undefined
                            }
                            serviceId={service.id}
                        />
                    ),
                    name: 'subsequentServices',
                    title: translate.subsequentService.subsequentServices,
                },
                {
                    content: <ServiceDeclarationActivityList serviceId={service.id} />,
                    name: 'declarationActivities',
                    title: translate.serviceDeclarationRules.declarationActivities,
                },
                {
                    content: <NzaServiceDeclarationRuleAddEdit serviceId={service.id} />,
                    name: 'nzaServiceDeclarationRules',
                    title: translate.serviceDeclarationRules.serviceDeclarationRules,
                },
                {
                    content: <InsurerServiceDeclarationRulesList serviceId={service.id} />,
                    name: 'insurerDeclarationRules',
                    title: translate.serviceDeclarationRules.insurerServiceDeclarationRules,
                },
                {
                    content: <PatientInvoiceServicePrices serviceId={service.id} />,
                    name: 'patientInvoicing',
                    title: translate.patientInvoiceServicePrices.patientInvoicing,
                }
            );
        }

        if (
            hasServiceActivities &&
            hasOneAuthorization([ManagerAuthorization.financeFunding, ManagerAuthorization.serviceDeclarationRules])
        ) {
            tabs.push({
                content: <FundingServiceParts service={service} />,
                name: 'fundingServicePart',
                title: translate.finance.fundingServicePart.shortName,
            });
        }

        if (service.serviceType === ServiceType.examination && hasOneAuthorization(ManagerAuthorization.graderPools)) {
            tabs.push({
                content: <ServiceGraderPool serviceId={service.id} />,
                name: 'graderPool',
                title: translate.serviceGraderPool.serviceGraderPool,
            });
        }
        if (hasOneAuthorization(ManagerAuthorization.servicesRead)) {
            tabs.push(
                {
                    content: <ServiceWorkflow serviceId={service.id} />,
                    name: 'serviceWorkflow',
                    title: translate.service.serviceWorkflow,
                },
                {
                    content: (
                        <PayableServiceActivitiesList
                            serviceCode={service.code}
                            serviceId={service.id}
                        />
                    ),
                    name: 'serviceCompensationPayouts',
                    title: translate.service.compensationPayouts,
                }
            );
        }
    }

    return (
        <Loading
            item={service}
            loaded={(s) => (
                <SideNavigationTemplate
                    breadcrumb={[
                        { text: translate.setting.settings },
                        {
                            text: translate.service.services,
                            url: hasOneAuthorization(ManagerAuthorization.servicesRead) ? '/services' : undefined,
                        },
                    ]}
                    infoBlock={<ServiceBlock service={s} />}
                    tabs={tabs}
                />
            )}
        />
    );

    async function loadServiceDetails(serviceId: string) {
        const url = `/api/services/${serviceId}`;
        const result = await fetchJson({ method: 'GET', signal: abortSignal, url });
        setService(result.json);

        const activities = await getServiceActivities(serviceId, abortSignal);
        setHasServiceActivities(activities.length > 0);
    }
}
